import { parseApiDateUtc } from '@/lib/api-date'
import { priorityLabel, statusLabel } from '@/lib/ticket-display'
import { isAssignmentSystemPseudoMessage } from '@/components/tickets/TicketMessageThread'
import type { TicketDetailMessage, TicketTimelineItem } from '@/types/api'

const ASSIGNMENT_SYSTEM_SENDER_EMAIL = 'assignment-system@servicedesk.local'

/** События таймлайна для переписки: всё, кроме самих сообщений чата. */
const TIMELINE_CHAT_SYSTEM_CATEGORIES = new Set([
  'assignment',
  'redistribution',
  'status',
  'priority',
  'created',
  'tag',
  'help',
  'review',
])

/** «Сейчас в перераспределении» — синтетика на now, в историю чата не кладём. */
export function isChatSystemTimelineItem(item: TicketTimelineItem): boolean {
  if (!TIMELINE_CHAT_SYSTEM_CATEGORIES.has(item.category)) return false
  if (item.activity_type === 'ticket_redistribution_active') return false
  if (String(item.id || '').startsWith('delegation-active:')) return false
  return true
}

function humanizeAssignmentActorName(value: string | null | undefined): string {
  const raw = String(value || '').trim()
  if (!raw) return 'Система'
  const lower = raw.toLowerCase()
  if (
    lower.includes('мозгобот') ||
    lower.includes('ai assistant') ||
    lower.includes('ai-assistant') ||
    lower === 'ai'
  ) {
    return 'Мозгобот'
  }
  if (
    lower.includes('автоперераспред') ||
    lower.includes('auto_redistribution')
  ) {
    return 'Автоперераспределение'
  }
  if (
    lower.includes('автораспредел') ||
    lower.includes('автоназнач') ||
    lower.includes('auto_distribution')
  ) {
    return 'Автораспределение'
  }
  return raw
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

function idOrNull(value: unknown): string | null {
  if (value == null) return null
  const s = String(value).trim()
  return s && s.toLowerCase() !== 'null' && s.toLowerCase() !== 'none' ? s : null
}

/** Имя назначенного: «неназначено» вместо пустого / «Не назначен». */
function assigneeDisplayName(value: unknown, fallback = 'неназначено'): string {
  const raw = String(value ?? '').trim()
  if (!raw || /^не\s*назначен/i.test(raw) || raw === '—' || raw.toLowerCase() === 'none') {
    return fallback
  }
  return humanizeAssignmentActorName(raw)
}

/** Системные исполнители назначения (не человек и не Мозгобот). */
const SYSTEM_ASSIGNMENT_ACTORS: Record<string, string> = {
  auto_distribution: 'Автораспределение',
  tag_rule: 'Правило меток',
  trigger: 'Триггер',
  emergency: 'Аварийный режим',
}

/**
 * Кто изменил назначение. Решает записанный исполнитель, а не «на кого назначили»:
 * оператор, назначивший обращение на Мозгобота или снявший с него, остаётся собой.
 */
function resolveTimelineChangerName(
  item: TicketTimelineItem,
  source: string,
  details: Record<string, unknown>,
  changerId: string | null,
): string {
  const kind = item.actor_kind
  if (kind === 'ai') return 'Мозгобот'
  if (kind === 'user') return humanizeAssignmentActorName(item.actor_name)
  if (kind === 'system') {
    return SYSTEM_ASSIGNMENT_ACTORS[source] || humanizeAssignmentActorName(item.actor_name)
  }
  // Ответ без actor_kind (старый бэкенд): человек, если записан его id.
  if (changerId) return humanizeAssignmentActorName(item.actor_name)
  if (SYSTEM_ASSIGNMENT_ACTORS[source]) return SYSTEM_ASSIGNMENT_ACTORS[source]
  if (source.startsWith('ai') || details.is_ai === true) return 'Мозгобот'
  return humanizeAssignmentActorName(item.actor_name)
}

/** Причина из деталей назначения / перераспределения (без служебных ярлыков вроде «ручное»). */
function timelineReasonText(
  ...candidates: Array<unknown>
): string | null {
  const skip = new Set([
    'ручное изменение',
    'manual',
    'автораспределение',
    'auto_distribution',
    'мозгобот',
    'ai',
    'триггер',
    'trigger',
    'правило меток',
    'tag_rule',
  ])
  for (const value of candidates) {
    const text = String(value ?? '').trim()
    if (!text) continue
    if (skip.has(text.toLowerCase())) continue
    return text
  }
  return null
}

function withReason(text: string, reason: string | null | undefined): string {
  const r = String(reason || '').trim()
  if (!r) return text
  if (text.toLowerCase().includes(r.toLowerCase())) return text
  return `${text}. Причина: ${r}`
}

function formatAssignmentSystemTextFromTimeline(item: TicketTimelineItem): string {
  if (item.category === 'assignment') {
    const details = asRecord(item.details) || {}
    const changes = asRecord(details.changes) || {}
    const assigned = asRecord(changes.assigned_to) || {}

    const oldId = idOrNull(assigned.old ?? details.old_assigned_to)
    const newId = idOrNull(assigned.new ?? details.new_assigned_to ?? details.operator_id)
    const oldName = assigneeDisplayName(assigned.old_name ?? details.old_assigned_name)
    const newName = assigneeDisplayName(
      assigned.new_name ?? details.new_assigned_name ?? details.operator_name,
      'неизвестный',
    )
    const changerId = idOrNull(
      assigned.changed_by ?? assigned.assigned_by ?? assigned.unassigned_by ?? item.actor_id,
    )
    const source = String(
      assigned.source ?? details.assignment_source ?? item.activity_type ?? '',
    ).toLowerCase()

    const changer = resolveTimelineChangerName(item, source, details, changerId)
    const hadOld = Boolean(oldId)
    const hasNew = Boolean(newId)
    const changerIsOld = Boolean(changerId && oldId && changerId === oldId)
    const changerIsNew = Boolean(changerId && newId && changerId === newId)
    const changerIsMozgobot = changer === 'Мозгобот'
    const changerIsAuto = changer === 'Автораспределение'
    const changerIsRule = changer === 'Правило меток' || changer === 'Триггер' || changer === 'Аварийный режим'
    const fromLabel = changerIsOld ? 'себя' : oldName
    const toLabel = changerIsNew ? 'себя' : newName
    const reason = timelineReasonText(
      assigned.reason_label,
      details.reason_label,
      details.entry_reason_label,
      details.reason,
    )

    if (hasNew && !hadOld) {
      if (changerIsNew || (changerIsMozgobot && newName === 'Мозгобот')) {
        return withReason(`${changer} назначил на себя`, reason)
      }
      if (changerIsAuto) {
        return withReason(`Автоназначение: назначено на ${newName}`, reason)
      }
      if (changerIsRule) return withReason(`${changer}: назначено на ${newName}`, reason)
      return withReason(`${changer} назначил на ${newName}`, reason)
    }
    if (hasNew && hadOld) {
      if (changerIsAuto) {
        return withReason(`Автоназначение: переназначено с ${oldName} на ${newName}`, reason)
      }
      if (changerIsRule) {
        return withReason(`${changer}: переназначено с ${oldName} на ${newName}`, reason)
      }
      return withReason(`${changer} переназначил с ${fromLabel} на ${toLabel}`, reason)
    }
    if (hadOld && !hasNew) {
      if (changerIsOld) return withReason(`${changer} снял назначение с себя`, reason)
      if (changerIsAuto) {
        return withReason(`Автоназначение: переведено в неназначено (было: ${oldName})`, reason)
      }
      if (changerIsRule) {
        return withReason(`${changer}: переведено в неназначено (было: ${oldName})`, reason)
      }
      return withReason(`${changer} перевел в неназначено (было: ${oldName})`, reason)
    }

    const summary = (item.summary || '').trim()
    if (summary) return withReason(summary, reason)
    return withReason(`${changer} изменил назначение`, reason)
  }

  if (item.category === 'created') {
    const summary = (item.summary || '').trim()
    const desc = (item.description || '').trim()
    if (desc) return desc
    if (summary) return `Обращение создано: ${summary}`
    return 'Обращение создано'
  }

  if (item.category === 'status') {
    const details = asRecord(item.details) || {}
    const changes = asRecord(details.changes) || {}
    const status = asRecord(changes.status) || {}
    const oldRaw = status.old ?? details.old_status
    const newRaw = status.new ?? details.new_status
    const actor = humanizeAssignmentActorName(
      String(item.actor_name ?? status.changed_by_name ?? details.changed_by_name ?? 'Система'),
    )
    if (oldRaw != null && newRaw != null && String(oldRaw) && String(newRaw)) {
      let text = `${actor} изменил статус: ${statusLabel(String(oldRaw))} → ${statusLabel(String(newRaw))}`
      if (String(newRaw) === 'pending') {
        const reason = String(status.pending_reason ?? details.pending_reason ?? '').trim()
        const comment = String(status.pending_comment ?? details.pending_comment ?? '').trim()
        if (reason) text += `. Причина: ${reason}`
        if (comment) text += `. Комментарий: ${comment}`
      }
      return text
    }
    const summary = (item.summary || '').trim()
    if (summary) return `${actor}: ${summary}`
    return `${actor} изменил статус`
  }

  if (item.category === 'priority') {
    const details = asRecord(item.details) || {}
    const changes = asRecord(details.changes) || {}
    const priority = asRecord(changes.priority) || {}
    const oldRaw = priority.old ?? details.old_priority
    const newRaw = priority.new ?? details.new_priority
    const actor = humanizeAssignmentActorName(
      String(item.actor_name ?? priority.changed_by_name ?? details.changed_by_name ?? 'Система'),
    )
    if (oldRaw != null && newRaw != null && String(oldRaw) && String(newRaw)) {
      return `${actor} изменил приоритет: ${priorityLabel(String(oldRaw))} → ${priorityLabel(String(newRaw))}`
    }
    const summary = (item.summary || '').trim()
    if (summary) return `${actor} изменил приоритет: ${summary}`
    return `${actor} изменил приоритет`
  }

  if (item.category === 'tag') {
    const summary = (item.summary || '').trim()
    const title = (item.title || 'Метка').trim()
    return summary ? `${title}: ${summary}` : title
  }

  if (item.category === 'redistribution') {
    const details = asRecord(item.details) || {}
    const assignedName = assigneeDisplayName(details.assigned_operator_name, 'основной оператор')
    const delegateName = assigneeDisplayName(details.delegate_operator_name, 'временный оператор')
    const duration = String(details.duration_text || '').trim()
    const activity = String(item.activity_type || '')
    const id = String(item.id || '')

    if (activity === 'ticket_redistributed' || id.startsWith('delegation-created:')) {
      const reason = timelineReasonText(details.entry_reason_label, details.reason_label)
      return withReason(
        `Автоперераспределение: ${assignedName} → ${delegateName}`,
        reason,
      )
    }

    if (activity === 'ticket_redistribution_revoked' || id.startsWith('delegation-revoked:')) {
      const reason = timelineReasonText(details.reason_label)
      let text = `Снято перераспределение с ${delegateName} (вернулось к ${assignedName})`
      if (duration) text += `. Было: ${duration}`
      return withReason(text, reason)
    }

    const summary = (item.summary || '').trim()
    const title = (item.title || 'Перераспределение').trim()
    return summary ? `${title}. ${summary}` : title
  }

  if (item.category === 'help') {
    const summary = (item.summary || '').trim()
    return summary ? `Запрошена помощь: ${summary}` : 'Запрошена помощь'
  }

  if (item.category === 'review') {
    return (item.title || 'Запрошен отзыв').trim()
  }

  const summary = (item.summary || '').trim()
  const title = (item.title || '').trim()
  if (summary && title && summary !== title) return `${title}. ${summary}`
  return summary || title || 'Системное событие'
}

function timelineItemToSystemMessage(
  item: TicketTimelineItem,
  ticketId: string,
): TicketDetailMessage {
  // Нормализуем к UTC ISO со суффиксом Z — как у сообщений после parseApiDateUtc
  const parsed = parseApiDateUtc(item.created_at)
  const createdAt = parsed ? parsed.toISOString() : item.created_at
  return {
    id: `timeline-sys:${item.id}`,
    ticket_id: ticketId,
    user_id: null,
    message_type: 'system',
    content: formatAssignmentSystemTextFromTimeline(item),
    sender_name: 'Система',
    sender_email: ASSIGNMENT_SYSTEM_SENDER_EMAIL,
    is_internal: true,
    created_at: createdAt,
  }
}

/** Единый разбор дат API (naive UTC) для сортировки переписки и системных событий. */
function messageCreatedAtMs(iso: string | null | undefined): number {
  return parseApiDateUtc(iso)?.getTime() ?? 0
}

export function mergeMessagesWithTimelineSystemEvents(
  messages: TicketDetailMessage[],
  timelineItems: TicketTimelineItem[],
  ticketId: string,
): TicketDetailMessage[] {
  if (!timelineItems.length) return messages
  const pseudo = timelineItems.map((item) => timelineItemToSystemMessage(item, ticketId))
  const merged = [...messages, ...pseudo]
  merged.sort((a, b) => {
    const dt = messageCreatedAtMs(a.created_at) - messageCreatedAtMs(b.created_at)
    if (dt !== 0) return dt
    const aSys = isAssignmentSystemPseudoMessage(a)
    const bSys = isAssignmentSystemPseudoMessage(b)
    if (aSys !== bSys) return aSys ? -1 : 1
    return a.id.localeCompare(b.id)
  })
  return merged
}
